/**
 * Calendar-date helpers for due dates (date-only, no time-of-day).
 * Avoid `new Date("YYYY-MM-DD")` and `toISOString().slice(0, 10)` — both are
 * timezone-sensitive and can shift the calendar day.
 */

export function formatYmd(year, monthIndex, day) {
  return `${year}-${String(monthIndex + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** Format a Date using its UTC Y/M/D (SheetJS / Excel serial convention). */
export function formatUtcYmd(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return formatYmd(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

/** Local midnight for a YYYY-MM-DD (or leading ISO date) string. */
export function localDateFromYmd(value) {
  if (value == null || value === "") return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  const str = String(value).trim();
  const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;

  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  const date = new Date(year, monthIndex, day);
  if (
    Number.isNaN(date.getTime()) ||
    date.getFullYear() !== year ||
    date.getMonth() !== monthIndex ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
}

/**
 * Normalize spreadsheet / form due-date values to YYYY-MM-DD.
 * Excel Date objects and serials encode the calendar day in UTC components.
 */
export function parseDueDate(value) {
  if (value == null || value === "") return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return formatUtcYmd(value);
  }

  if (typeof value === "number" && Number.isFinite(value) && value > 20000 && value < 80000) {
    const utc = new Date(Math.round((value - 25569) * 86400 * 1000));
    return formatUtcYmd(utc);
  }

  const str = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;

  // ISO datetime — keep the calendar date prefix, do not re-interpret via local TZ.
  const isoPrefix = str.match(/^(\d{4}-\d{2}-\d{2})[T\s]/);
  if (isoPrefix) return isoPrefix[1];

  const dmy = str.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (dmy) {
    const [, day, month, year] = dmy;
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }

  return null;
}

/** Whole days past due (0 if due today, future, or invalid). Uses local calendar days. */
export function daysLateFromDueDate(dueDate) {
  const due = localDateFromYmd(dueDate);
  if (!due) return 0;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.max(0, Math.floor((today - due) / (1000 * 60 * 60 * 24)));
}
